import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Email verification page - handles the verification token from email link
 */
const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and toast are stable module-level references
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${API}/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = await response.json();
        
        if (response.ok) {
          if (data.already_verified) {
            setStatus('already_verified');
            setMessage('Your email is already verified');
          } else {
            setStatus('success');
            setMessage(data.message || 'Email verified successfully!');
            setUserInfo({ email: data.email, username: data.username });
            toast.success('Email verified! Welcome to the pack!');
          }
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('Failed to verify email. Please try again.');
      }
    };

    verifyEmail();
  }, [token]);

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center py-8">
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-semibold mb-2">Verifying your email...</h2>
            <p className="text-muted-foreground">Just a moment while we confirm your email address.</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-green-700 mb-2">Email Verified!</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            {userInfo && (
              <p className="text-sm text-muted-foreground mb-6">
                Welcome, <span className="font-medium">{userInfo.username}</span>!
              </p>
            )}
            <Button onClick={() => navigate('/login')} className="gap-2">
              Continue to Login <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        );
      
      case 'already_verified':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-blue-700 mb-2">Already Verified</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button onClick={() => navigate('/login')} className="gap-2">
              Go to Login <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-red-700 mb-2">Verification Failed</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                The link may have expired or is invalid.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/login')}>
                  Go to Login
                </Button>
                <Button onClick={() => navigate('/settings')}>
                  Resend Verification
                </Button>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          <CardTitle>Email Verification</CardTitle>
          <CardDescription>
            Confirming your email address
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
